/**
 * Navbar - 顶部导航栏
 * 包含 Logo、导航链接、用户菜单（登录/注册或用户信息）
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { 
  Home, 
  User, 
  BookOpen, 
  Wallet, 
  LogOut, 
  ChevronDown,
  Globe
} from 'lucide-react';
import Button from '../ui/Button';

const navItems = [
  { path: '/', label: '首页', icon: Home },
  { path: '/public', label: '公开社区', icon: Globe },
];

// 用户头像组件
function UserAvatar({ src, fallback }: { src?: string; fallback: string }) {
  return (
    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium text-sm overflow-hidden">
      {src ? (
        <img src={src} alt="avatar" className="w-full h-full object-cover" />
      ) : (
        fallback
      )}
    </div>
  );
}

// 用户下拉菜单
function UserDropdown() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const menuItems = [
    { label: '个人中心', icon: User, path: '/profile', color: 'text-slate-700' },
    { label: '我的绘本', icon: BookOpen, path: '/profile?tab=stories', color: 'text-slate-700' },
    { label: '我的钱包', icon: Wallet, path: '/profile?tab=wallet', color: 'text-slate-700' },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-100 transition-colors"
      >
        <UserAvatar 
          src={user?.avatar} 
          fallback={user?.nickname?.[0]?.toUpperCase() || 'U'} 
        />
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-slate-800">{user?.nickname}</p>
          <p className="text-xs text-slate-500">余额 ¥{user?.balance?.toFixed(2) || '0.00'}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* 遮罩层 */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* 下拉菜单 */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-56 rounded-2xl p-2 bg-white shadow-sm border border-slate-200 z-50 overflow-hidden flex flex-col gap-2"
            >
              {/* 用户信息头部 */}
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-2">
                <div className="flex items-center gap-2">
                  <UserAvatar 
                    src={user?.avatar} 
                    fallback={user?.nickname?.[0]?.toUpperCase() || 'U'} 
                  />
                  <div>
                    <p className="font-medium text-slate-800">{user?.nickname}</p>
                    <p className="text-xs text-slate-500">{user?.email || user?.phone}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between" style={{ marginTop: '8px' }}>
                  <span className="text-sm text-slate-600">余额</span>
                  <span className="font-bold text-indigo-600">¥{user?.balance?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              {/* 菜单项 */}
              <div className="flex flex-col gap-2">
                {menuItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.path);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-2 rounded-xl hover:bg-slate-100 transition-colors text-left p-2"
                  >
                    <item.icon className={`w-4 h-4 ${item.color}`} />
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </button>
                ))}

                <div style={{ margin: '8px 0' }} className="border-t border-slate-100" />

                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                    navigate('/');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors text-left"
                >
                  <LogOut className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-600">退出登录</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// 未登录状态的用户区域
function GuestActions() {
  const navigate = useNavigate();
  const { allowRegister } = useSiteConfig();

  return (
    <div className="flex items-center gap-3">
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => navigate('/login')}
      >
        登录
      </Button>
      {allowRegister && (
        <Button 
          size="sm"
          className="bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          onClick={() => navigate('/register')}
        >
          免费注册
        </Button>
      )}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="w-full flex justify-center px-4 sm:px-6 lg:px-8">
      <nav className="sticky top-0 z-40 w-full max-w-7xl bg-white/95 border-b border-slate-200/50 md:rounded-b-2xl">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl md:text-2xl">📚</span>
            <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              BadiBook
            </span>
          </Link>

          {/* Desktop Navigation - 居中 */}
          <div className="hidden md:flex items-center justify-center flex-1">
            <div className="flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-indigo-600 text-white rounded-xl'
                      : 'text-slate-600 hover:bg-slate-100 rounded-xl'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* 用户区域 - 仅 PC 端显示 */}
          <div className="hidden md:flex items-center gap-3">
            {/* 用户菜单 */}
            {isAuthenticated ? (
              <UserDropdown />
            ) : (
              <GuestActions />
            )}
          </div>
        </div>
      </nav>
    </div>
  );
}
